import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

const createEmptyWhereTree = () => ({ logic: 'AND', conditions: [], groups: [] });

const DEFAULT_LOOKUP_TRANSFORM = {
  trim_after_plus: true,
  truncate_last_chars: 2,
  asterisk_replacement: '.',
  dot_wildcard_enabled: true,
};
const DEFAULT_MATCH_PRODUCT_FIELDS = [
  'id',
  'accountId',
  'productCode',
  'imageUrl',
  'manufacturerName',
  'manufacturerPartNumber',
  'name',
];
const DEFAULT_PRODUCT_IMAGE_BASE_URL = 'https://www.gearypacific.com//ASSETS/IMAGES/ITEMS/ZOOM/';

const ensureMatchLookupConfig = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const rawFields = Array.isArray(source.product_fields) ? source.product_fields : [];
  const cleaned = [];
  rawFields.forEach((field) => {
    if (typeof field !== 'string') return;
    const name = field.trim();
    if (name && !cleaned.includes(name)) {
      cleaned.push(name);
    }
  });
  if (!cleaned.length) {
    cleaned.push(...DEFAULT_MATCH_PRODUCT_FIELDS);
  } else {
    if (!cleaned.includes('productCode')) cleaned.push('productCode');
    if (!cleaned.includes('imageUrl')) cleaned.push('imageUrl');
  }
  const enforce =
    typeof source.enforce_complete_match === 'boolean'
      ? source.enforce_complete_match
      : true;
  return {
    product_fields: cleaned,
    enforce_complete_match: enforce,
  };
};


const normalizeWhereTree = (tree) => {
  const base = createEmptyWhereTree();
  if (!tree || typeof tree !== 'object') {
    return base;
  }
  const logic = String(tree.logic || '').toUpperCase() === 'OR' ? 'OR' : 'AND';
  const conditions = Array.isArray(tree.conditions)
    ? tree.conditions
        .filter((cond) => cond && typeof cond === 'object')
        .map((cond) => ({
          field: typeof cond.field === 'string' ? cond.field : '',
          op: typeof cond.op === 'string' ? cond.op : 'eq',
          value: cond.hasOwnProperty('value') ? cond.value : '',
        }))
    : [];
  const groups = Array.isArray(tree.groups)
    ? tree.groups
        .filter((grp) => grp && typeof grp === 'object')
        .map((grp) => normalizeWhereTree(grp))
    : [];
  return { logic, conditions, groups };
};

const hasWhereContent = (tree) => {
  if (!tree || typeof tree !== 'object') return false;
  if (Array.isArray(tree.conditions) && tree.conditions.some((cond) => cond && cond.field)) {
    return true;
  }
  if (Array.isArray(tree.groups)) {
    return tree.groups.some((grp) => hasWhereContent(grp));
  }
  return false;
};

const AhriData = () => {
  const { getAuthHeaders } = useAuth();
  const [cfg, setCfg] = useState({
    token_url: '', grant_type: 'client_credentials', client_id: '', client_secret: '', scope: '',
    data_url: '', userUniqueId: '', content_type: 'application/json', program_names: [],
    outdoor_brand_names: [], indoor_brand_names: [],
    pinecone: { api_key: '', host: '', index: '' },
    openai: { api_key: '', base_url: '', embedding_model: 'text-embedding-3-small', completions_model: 'gpt-4.1' },
    solr: { base_url: '', username: '', password: '', verify_ssl: true, shards: 1, replicas: 1 },
    page_size: 200, threads: 2, mapping_threads: 2, vectorize_enabled: true,
    // Network/security
    verify_ssl: true, use_system_proxy: true, proxy_url: '',
    // Optional manual bearer token to bypass OAuth call
    access_token_override: '',
    lookup_transform: { ...DEFAULT_LOOKUP_TRANSFORM },
    product_image_fallback_base_url: DEFAULT_PRODUCT_IMAGE_BASE_URL,
    match_product_lookup: ensureMatchLookupConfig(),
  });
  const [activeTab, setActiveTab] = useState('settings');
  const [jobs, setJobs] = useState([]);
  const [polling, setPolling] = useState(false);
  const [mappingState, setMappingState] = useState({});
  const [mappingJobs, setMappingJobs] = useState([]);
  const [mappingJobsLoading, setMappingJobsLoading] = useState(false);
  const [mappingJobsError, setMappingJobsError] = useState('');
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingError, setMappingError] = useState('');
  const [mappingStarting, setMappingStarting] = useState(false);
  const [mappingStopping, setMappingStopping] = useState(false);
  const [mappingScopeSelection, setMappingScopeSelection] = useState('full');
  const [mappingIdsInput, setMappingIdsInput] = useState('');
  const [programOptions, setProgramOptions] = useState([]);
  const [productFieldOptions, setProductFieldOptions] = useState([]);
  const [productFieldOptionsLoading, setProductFieldOptionsLoading] = useState(false);
  const [productFieldOptionsError, setProductFieldOptionsError] = useState('');
  const [productFieldFilter, setProductFieldFilter] = useState('');
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [embDebug, setEmbDebug] = useState(false);
  const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
  const matchLookupCfg = ensureMatchLookupConfig(cfg.match_product_lookup);
  const updateMatchLookup = useCallback((partial) => {
    setCfg((prev) => ({
      ...prev,
      match_product_lookup: {
        ...ensureMatchLookupConfig(prev.match_product_lookup),
        ...partial,
      },
    }));
  }, []);
  const matchLookupSelected = useMemo(
    () => new Set(matchLookupCfg.product_fields || []),
    [matchLookupCfg.product_fields],
  );
  const filteredProductFieldOptions = useMemo(() => {
    const list = productFieldOptions || [];
    const filterText = productFieldFilter.trim().toLowerCase();
    const deduped = Array.from(new Set(list));
    const sorted = deduped.sort((a, b) => a.localeCompare(b));
    if (!filterText) {
      return sorted;
    }
    return sorted.filter((field) => field.toLowerCase().includes(filterText));
  }, [productFieldOptions, productFieldFilter]);
  const [logOpen, setLogOpen] = useState(false);
  const [logText, setLogText] = useState('');
  const [schemasText, setSchemasText] = useState('[]');
  const [schemasError, setSchemasError] = useState('');
  const [schemaProg, setSchemaProg] = useState('');
  const [schemaLines, setSchemaLines] = useState('');
  const [schemaProductFields, setSchemaProductFields] = useState([]);
  const [schemaSelectedFields, setSchemaSelectedFields] = useState([]);
  const [schemaCollection, setSchemaCollection] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerWidthPct, setDrawerWidthPct] = useState(50); // default 50%
  const [resizing, setResizing] = useState(false);
  const [qbTree, setQbTree] = useState(createEmptyWhereTree());
  const [whereJsonText, setWhereJsonText] = useState(JSON.stringify(createEmptyWhereTree(), null, 2));
  const [whereJsonDirty, setWhereJsonDirty] = useState(false);
  const [whereJsonError, setWhereJsonError] = useState('');
  const [settingsSection, setSettingsSection] = useState('openai'); // vertical settings tabs
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [verifyData, setVerifyData] = useState(null);
  const [solrDeleting, setSolrDeleting] = useState(false);
  const [productSolrDeleting, setProductSolrDeleting] = useState(false);
  const [programPurgeName, setProgramPurgeName] = useState('');
  const [programPurging, setProgramPurging] = useState(false);
  const lookupTransform = useMemo(
    () => ({ ...DEFAULT_LOOKUP_TRANSFORM, ...(cfg.lookup_transform || {}) }),
    [cfg.lookup_transform]
  );
  const updateLookupTransform = useCallback((partial) => {
    setCfg((prev) => ({
      ...prev,
      lookup_transform: {
        ...DEFAULT_LOOKUP_TRANSFORM,
        ...(prev.lookup_transform || {}),
        ...partial,
      },
    }));
  }, []);
  const [sampleProgram, setSampleProgram] = useState('');
  const [sampleFields, setSampleFields] = useState([]);
  const [sampleResults, setSampleResults] = useState([]);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleError, setSampleError] = useState('');
  const deleteSolrCollection = async () => {
    if (!window.confirm('Delete the AHRI data collection for this account? This cannot be undone.')) {
      return;
    }
    setSolrDeleting(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/solr/collection/delete`, {
        method: 'POST',
        headers,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        window.alert(`Delete failed: ${data?.detail || resp.status}`);
      } else {
        window.alert('AHRI Solr collection deleted. Run a full import to recreate it.');
      }
    } catch (error) {
      window.alert(`Delete failed: ${error}`);
    } finally {
      setSolrDeleting(false);
    }
  };
  const deleteProductCollection = async () => {
    if (!window.confirm('Delete the Products data collection for this account? This cannot be undone.')) {
      return;
    }
    setProductSolrDeleting(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/products/solr/collection/delete`, {
        method: 'POST',
        headers,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        window.alert(`Delete failed: ${data?.detail || resp.status}`);
      } else {
        window.alert('Products Solr collection deleted. Run a product full import to recreate it.');
      }
    } catch (error) {
      window.alert(`Delete failed: ${error}`);
    } finally {
      setProductSolrDeleting(false);
    }
  };
  const handleProgramPurge = async () => {
    const program = (programPurgeName || '').trim();
    if (!program) {
      window.alert('Enter a program name to delete.');
      return;
    }
    if (!window.confirm(`Delete AHRI data for program "${program}" from Postgres and Solr? This cannot be undone.`)) {
      return;
    }
    setProgramPurging(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/program/${encodeURIComponent(program)}`, {
        method: 'DELETE',
        headers,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        window.alert(`Program delete failed: ${data?.detail || resp.status}`);
      } else {
        const dbCount = typeof data?.db_deleted === 'number' ? data.db_deleted : '0';
        const solrStatus = data?.solr_request_sent ? 'Solr delete submitted.' : 'Solr delete failed or not configured.';
        window.alert(`Deleted ${dbCount} DB rows for ${program}. ${solrStatus}`);
      }
    } catch (err) {
      window.alert(`Program delete failed: ${err}`);
    } finally {
      setProgramPurging(false);
    }
  };
  const [verifyProgram, setVerifyProgram] = useState('');
  const [confirmSchemaOpen, setConfirmSchemaOpen] = useState(false);
  const [pendingSchema, setPendingSchema] = useState(null);
  const activeJob = useMemo(() => {
    if (!Array.isArray(jobs) || jobs.length === 0) return null;
    return jobs.find((job) => ['running', 'pending', 'queued', 'cancelling'].includes(job.status));
  }, [jobs]);
  const activeJobProgress = useMemo(() => {
    if (!activeJob) return { percent: 0, label: 'No import running' };
    const total = Number(activeJob.total || 0);
    const progress = Number(activeJob.progress || 0);
    if (!total || total <= 0 || progress <= 0) {
      return { percent: 0, label: 'Preparing...' };
    }
    const pct = Math.min(100, Math.round((progress / total) * 100));
    return { percent: pct, label: `${progress} / ${total}` };
  }, [activeJob]);

  const mappingStatus = mappingState?.status || '';
  const mappingStats = useMemo(() => {
    const total = Number(mappingState?.total || 0);
    const processed = Number(mappingState?.processed || 0);
    let percent = 0;
    if (total > 0) {
      percent = Math.min(100, Math.round((processed / total) * 100));
    } else if ((mappingStatus === 'completed' || mappingStatus === 'failed') && processed > 0) {
      percent = 100;
    }
    return { total, processed, percent };
  }, [mappingState, mappingStatus]);
  const mappingMatches = Number(mappingState?.matches || 0);
  const mappingCompleteMatches = Number(mappingState?.complete_matches || 0);
  const mappingUpdatedProducts = Number(mappingState?.updated_products || 0);
  const mappingScopeState = mappingState?.scope || 'full';
  const mappingRequestedIds = Array.isArray(mappingState?.requested_ids) ? mappingState.requested_ids : [];
  const mappingRequestedIdsMore = Number(mappingState?.requested_ids_more || 0);
  const mappingRequestedIdsTotal =
    Number(mappingState?.requested_ids_total || 0) || (mappingRequestedIds.length || 0);
  const mappingIdsParsedCount = useMemo(() => {
    if (!mappingIdsInput) return 0;
    return mappingIdsInput
      .split(/[\s,]+/)
      .map((id) => id.trim())
      .filter((id) => id.length > 0).length;
  }, [mappingIdsInput]);

  useEffect(() => {
    const onMove = (e) => {
      if (!resizing) return;
      const vw = Math.max(window.document.documentElement.clientWidth || 0, window.innerWidth || 0);
      const fromRightPx = Math.max(0, vw - (e.clientX || 0));
      let pct = (fromRightPx / vw) * 100;
      if (pct < 50) pct = 50;
      if (pct > 75) pct = 75;
      setDrawerWidthPct(pct);
    };
    const onUp = () => setResizing(false);
    if (resizing) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing]);

  const schemaFieldOptions = useMemo(() => {
    const names = new Set();
    (schemaLines || '')
      .split('\n')
      .forEach((line) => {
        const match = String(line || '').match(/^\s*([A-Za-z_][A-Za-z0-9_]*)\s*:/);
        if (match && match[1]) {
          names.add(match[1]);
        }
      });
    return Array.from(names);
  }, [schemaLines]);

  useEffect(() => {
    setSchemaProductFields((prev) => prev.filter((name) => schemaFieldOptions.includes(name)));
    setSchemaSelectedFields((prev) => {
      const filtered = prev.filter((name) => schemaFieldOptions.includes(name));
      if (filtered.length === 0 && schemaFieldOptions.length > 0) {
        return [...schemaFieldOptions];
      }
      return filtered;
    });
  }, [schemaFieldOptions]);

  useEffect(() => {
    if (!drawerOpen || whereJsonDirty) return;
    const normalized = normalizeWhereTree(qbTree);
    const nextText = JSON.stringify(normalized, null, 2);
    if (nextText !== whereJsonText) {
      setWhereJsonText(nextText);
    }
  }, [drawerOpen, qbTree, whereJsonDirty, whereJsonText]);

  useEffect(() => {
    const schemaSet = new Set();
    if (Array.isArray(cfg.program_schemas)) {
      cfg.program_schemas.forEach((schema) => {
        const prog = schema?.program;
        if (typeof prog === 'string' && prog.trim()) {
          schemaSet.add(prog.trim());
        }
      });
    }
    const legacySet = new Set();
    if (Array.isArray(cfg.program_names)) {
      cfg.program_names.forEach((prog) => {
        if (typeof prog === 'string' && prog.trim()) {
          legacySet.add(prog.trim());
        }
      });
    }
    const schemaOptions = Array.from(schemaSet);
    const legacyOptions = Array.from(legacySet);
    const nextOptions = schemaOptions.length > 0 ? schemaOptions : legacyOptions;
    const finalOptions = nextOptions.length > 0 ? nextOptions : ['AC'];
    setProgramOptions(finalOptions);
    setSelectedPrograms((prev) => {
      const filtered = prev.filter((prog) => finalOptions.includes(prog));
      if (filtered.length > 0) {
        return filtered;
      }
      return finalOptions;
    });
  }, [cfg.program_schemas, cfg.program_names]);

  const programLookupFieldMap = useMemo(() => {
    const map = {};
    (cfg.program_schemas || []).forEach((schema) => {
      const prog = (schema?.program || '').trim();
      if (!prog) return;
      const fields = Array.isArray(schema?.product_lookup_fields)
        ? schema.product_lookup_fields.filter((f) => typeof f === 'string' && f.trim())
        : [];
      map[prog] = fields;
    });
    return map;
  }, [cfg.program_schemas]);

  useEffect(() => {
    if (!sampleProgram && programOptions.length > 0) {
      setSampleProgram(programOptions[0]);
    }
  }, [programOptions, sampleProgram]);

  useEffect(() => {
    const available = programLookupFieldMap[sampleProgram] || [];
    setSampleFields((prev) => {
      if (!prev || prev.length === 0) {
        return available;
      }
      const filtered = prev.filter((field) => available.includes(field));
      if (filtered.length === prev.length) {
        return prev;
      }
      return filtered.length ? filtered : available;
    });
  }, [sampleProgram, programLookupFieldMap]);

  const loadConfig = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/ahri/config`, { headers: getAuthHeaders() });
      if (r.ok) {
        const j = await r.json();
        const cfgLoaded = { ...j?.config };
        cfgLoaded.lookup_transform = {
          ...DEFAULT_LOOKUP_TRANSFORM,
          ...(cfgLoaded?.lookup_transform || {})
        };
        if (typeof cfgLoaded.mapping_threads !== 'number') {
          cfgLoaded.mapping_threads = 2;
        }
        setCfg(prev => ({ ...prev, ...cfgLoaded }));
        try {
          setSchemasText(JSON.stringify(cfgLoaded?.program_schemas || [], null, 2));
          setSchemasError('');
        } catch {
          setSchemasText('[]');
          setSchemasError('');
        }
      }
    } catch {}
  };

  const loadJobs = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/ahri/jobs`, { headers: getAuthHeaders() });
      if (r.ok) {
        const j = await r.json();
        setJobs(j.jobs || []);
      }
    } catch {}
  };

  const loadMappingState = useCallback(async () => {
    try {
      setMappingLoading(true);
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/state`, { headers: getAuthHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setMappingState(data?.state || {});
        setMappingError('');
      } else {
        const data = await resp.json().catch(() => ({}));
        setMappingError(data?.detail || `HTTP ${resp.status}`);
      }
    } catch (err) {
      setMappingError(String(err));
    } finally {
      setMappingLoading(false);
    }
  }, [getAuthHeaders]);

  const loadMappingJobs = useCallback(async () => {
    try {
      setMappingJobsLoading(true);
      setMappingJobsError('');
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/jobs`, { headers: getAuthHeaders() });
      if (resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setMappingJobs(Array.isArray(data?.jobs) ? data.jobs : []);
      } else {
        const data = await resp.json().catch(() => ({}));
        setMappingJobsError(data?.detail || `HTTP ${resp.status}`);
      }
    } catch (err) {
      setMappingJobsError(String(err));
    } finally {
      setMappingJobsLoading(false);
    }
  }, [getAuthHeaders]);

  const startProductMapping = useCallback(async () => {
    if (mappingStarting) return;
    const payload = {};
    // Pass the max threads selected in the Mapping section so backend
    // uses the same value for this job without requiring a separate Save.
    if (typeof cfg.mapping_threads === 'number') {
      payload.mapping_threads = cfg.mapping_threads;
    }
    if (mappingScopeSelection === 'subset') {
      const parsedIds = (mappingIdsInput || '')
        .split(/[\s,]+/)
        .map((id) => id.trim())
        .filter((id) => id.length > 0);
      if (parsedIds.length === 0) {
        setMappingError('Enter at least one AHRI ID to run a targeted mapping job.');
        return;
      }
      payload.ahri_ids = parsedIds;
    }
    setMappingStarting(true);
    setMappingError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setMappingError(data?.detail || 'Unable to start mapping');
      } else {
        await loadMappingState();
        await loadMappingJobs();
      }
    } catch (err) {
      setMappingError(String(err));
    } finally {
      setMappingStarting(false);
    }
  }, [cfg.mapping_threads, headers, loadMappingState, mappingStarting, mappingScopeSelection, mappingIdsInput]);

  const stopProductMapping = useCallback(async () => {
    if (mappingStopping) return;
    setMappingStopping(true);
    setMappingError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/stop`, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setMappingError(data?.detail || 'Unable to stop mapping job');
      } else {
        await loadMappingState();
        await loadMappingJobs();
      }
    } catch (err) {
      setMappingError(String(err));
    } finally {
      setMappingStopping(false);
    }
  }, [headers, loadMappingJobs, loadMappingState, mappingStopping]);

  const runSamplePreview = useCallback(async () => {
    if (!sampleProgram) {
      setSampleError('Select a program to preview.');
      return;
    }
    setSampleLoading(true);
    setSampleError('');
    setSampleResults([]);
    try {
      const body = {
        program: sampleProgram,
        fields: sampleFields && sampleFields.length ? sampleFields : undefined,
        limit: 5,
        transform: lookupTransform,
      };
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/sample`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setSampleError(data?.detail || `Unable to run preview (HTTP ${resp.status})`);
        setSampleResults([]);
      } else {
        setSampleResults(Array.isArray(data?.samples) ? data.samples : []);
      }
    } catch (err) {
      setSampleError(String(err));
    } finally {
      setSampleLoading(false);
    }
  }, [sampleProgram, sampleFields, lookupTransform, headers]);

  const viewMappingJobLog = useCallback(async (jobId) => {
    if (!jobId) return;
    // Prefer in-memory log for the currently running job so entries (like query preview) appear immediately
    if (mappingState?.job_id && mappingState.job_id === jobId && Array.isArray(mappingState?.log)) {
      const lines = mappingState.log || [];
      setLogText(lines.length ? lines.join('\n') : 'No log entries yet.');
      setLogOpen(true);
      return;
    }
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/job/${jobId}`, { headers: getAuthHeaders() });
      if (resp.ok) {
        const data = await resp.json().catch(() => ({}));
        const log = data?.job?.log;
        setLogText(log && log.length ? log : 'No log entries found for this job.');
        setLogOpen(true);
      } else {
        const data = await resp.json().catch(() => ({}));
        alert(data?.detail || `Unable to load log (HTTP ${resp.status})`);
      }
    } catch (err) {
      alert(`Unable to load log: ${err}`);
    }
  }, [getAuthHeaders, mappingState]);

  const deleteMappingJob = useCallback(async (jobId) => {
    if (!jobId) return;
    if (!window.confirm('Delete this mapping job record?')) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/job/${jobId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        alert(data?.detail || `Delete failed (HTTP ${resp.status})`);
      } else {
        await loadMappingJobs();
      }
    } catch (err) {
      alert(`Delete failed: ${err}`);
    }
  }, [getAuthHeaders, loadMappingJobs]);

  useEffect(() => {
    (async () => {
      await loadConfig();
      await loadJobs();
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!polling) return;
    const id = setInterval(loadJobs, 5000);
    return () => clearInterval(id);
  }, [polling]);

  useEffect(() => {
    if (activeTab === 'mapping') {
      loadMappingState();
      loadMappingJobs();
    }
  }, [activeTab, loadMappingJobs, loadMappingState]);

  const mappingActive = mappingStatus === 'running' || mappingStatus === 'queued';

  useEffect(() => {
    if (activeTab !== 'mapping' || !mappingActive) return;
    const id = setInterval(() => {
      loadMappingState();
    }, 5000);
    return () => clearInterval(id);
  }, [activeTab, mappingActive, loadMappingState]);

  useEffect(() => {
    if (activeTab !== 'mapping') return;
    if (!mappingActive && mappingStatus) {
      loadMappingJobs();
    }
  }, [activeTab, mappingActive, mappingStatus, loadMappingJobs]);

  const loadProductFieldOptions = useCallback(async () => {
    try {
      setProductFieldOptionsLoading(true);
      setProductFieldOptionsError('');
      const resp = await fetch(`${API_BASE_URL}/api/v1/products/solr/schema/fields`, { headers: getAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      const options = Array.isArray(data?.fields) ? data.fields : [];
      setProductFieldOptions(options);
    } catch (err) {
      setProductFieldOptionsError(String(err?.message || err));
      setProductFieldOptions([]);
    } finally {
      setProductFieldOptionsLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (settingsSection !== 'match_lookup') return undefined;
    if (productFieldOptions.length === 0 && !productFieldOptionsLoading && !productFieldOptionsError) {
      loadProductFieldOptions();
    }
    return undefined;
  }, [settingsSection, productFieldOptions.length, productFieldOptionsLoading, productFieldOptionsError, loadProductFieldOptions]);

  const handleProgramSelectChange = (event) => {
    const values = Array.from(event.target.selectedOptions).map((option) => option.value);
    setSelectedPrograms(values);
  };
  const handleSelectAllPrograms = () => {
    setSelectedPrograms(programOptions);
  };

  const handleClearPrograms = () => {
    setSelectedPrograms([]);
  };

  const handleMappingThreadsChange = (value) => {
    const n = onNum(value, 1, 10);
    setCfg((prev) => ({
      ...prev,
      mapping_threads: n === '' ? prev.mapping_threads || 1 : n,
    }));
  };

  const save = async () => {
    const r = await fetch(`${API_BASE_URL}/api/v1/ahri/config`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(cfg)
    });
    alert(r.ok ? 'Saved' : 'Save failed');
  };

  const startSync = async () => {
    if (!selectedPrograms.length) {
      alert('Select at least one program to sync.');
      return;
    }
    const payload = { programs: selectedPrograms };
    const r = await fetch(`${API_BASE_URL}/api/v1/ahri/sync/start`, { method: 'POST', headers, body: JSON.stringify(payload) });
    const j = await r.json();
    if (r.ok) {
      setPolling(true);
      await loadJobs();
      alert(`Sync started (job ${j.job_id})`);
    } else {
      alert(`Sync failed to start: ${j?.detail || r.status}`);
    }
  };

  const cancelJob = async (jobId) => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/ahri/job/${jobId}/cancel`, { method: 'POST', headers });
      if (r.ok) {
        setPolling(true);
        await loadJobs();
      } else {
        const j = await r.json().catch(()=>({}));
        alert(`Cancel failed: ${j?.detail || r.status}`);
      }
    } catch (e) {
      alert('Cancel failed');
    }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm('Delete this job?')) return;
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/ahri/job/${jobId}`, { method: 'DELETE', headers });
      if (r.ok) {
        await loadJobs();
      } else {
        const j = await r.json().catch(()=>({}));
        alert(`Delete failed: ${j?.detail || r.status}`);
      }
    } catch (e) {
      alert('Delete failed');
    }
  };
  const startEmbeddings = async () => {
    const r = await fetch(`${API_BASE_URL}/api/v1/ahri/embeddings/start`, { method: 'POST', headers, body: JSON.stringify({ debug: embDebug }) });
    const j = await r.json();
    if (r.ok) {
      setPolling(true);
      await loadJobs();
      alert(`Embedding started (job ${j.job_id})`);
    } else {
      alert(`Embedding failed to start: ${j?.detail || r.status}`);
    }
  };

  const onNum = (val, min, max) => {
    const n = parseInt(val || '0', 10);
    if (isNaN(n)) return '';
    let m = n;
    if (typeof min === 'number') m = Math.max(min, m);
    if (typeof max === 'number') m = Math.min(max, m);
    return m;
  };

  return (
    <div className="container py-4">
      <h3 className="mb-3">AHRI Data</h3>

      {/* Tabs header */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'sync' ? 'active' : ''}`}
            onClick={() => setActiveTab('sync')}
          >
            Data Sync
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'mapping' ? 'active' : ''}`}
            onClick={() => setActiveTab('mapping')}
          >
            Product Mapping
          </button>
        </li>
      </ul>

      {/* Tab content */}
      {activeTab === 'settings' && (
        <div className="row">
          <div className="col-12 col-md-3 col-lg-2 mb-3">
            <ul className="nav nav-pills flex-column">
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'openai' ? 'active' : ''}`} onClick={()=>setSettingsSection('openai')}>OpenAI</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'pinecone' ? 'active' : ''}`} onClick={()=>setSettingsSection('pinecone')}>Pinecone</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'connection' ? 'active' : ''}`} onClick={()=>setSettingsSection('connection')}>AHRI Connection</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'schemas' ? 'active' : ''}`} onClick={()=>setSettingsSection('schemas')}>Program Schemas</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'solr' ? 'active' : ''}`} onClick={()=>setSettingsSection('solr')}>Solr</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'match_lookup' ? 'active' : ''}`} onClick={()=>setSettingsSection('match_lookup')}>Match Lookup</button>
              </li>
            </ul>
          </div>
          <div className="col-12 col-md-9 col-lg-10">
            {settingsSection === 'openai' && (
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="mb-3">OpenAI (AHRI)</h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">API Key</label>
                      <input type="password" className="form-control" value={cfg.openai?.api_key || ''} onChange={(e)=>setCfg({...cfg, openai: { ...(cfg.openai||{}), api_key: e.target.value }})} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Base URL (optional)</label>
                      <input className="form-control" value={cfg.openai?.base_url || ''} onChange={(e)=>setCfg({...cfg, openai: { ...(cfg.openai||{}), base_url: e.target.value }})} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Embedding Model</label>
                      <input className="form-control" value={cfg.openai?.embedding_model || ''} onChange={(e)=>setCfg({...cfg, openai: { ...(cfg.openai||{}), embedding_model: e.target.value }})} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Completions Model</label>
                      <input className="form-control" value={cfg.openai?.completions_model || ''} onChange={(e)=>setCfg({...cfg, openai: { ...(cfg.openai||{}), completions_model: e.target.value }})} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">System Prompt</label>
                      <textarea className="form-control" rows={3} value={cfg.openai?.system_prompt || ''} onChange={(e)=>setCfg({...cfg, openai: { ...(cfg.openai||{}), system_prompt: e.target.value }})} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {settingsSection === 'pinecone' && (
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="mb-3">Pinecone (AHRI)</h6>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="form-check form-switch">
                        <input className="form-check-input" type="checkbox" id="vec_enabled" checked={!!cfg.vectorize_enabled} onChange={(e)=>setCfg({...cfg, vectorize_enabled: e.target.checked})} />
                        <label className="form-check-label" htmlFor="vec_enabled">
                          Enable vectorizing to Pinecone (when off, sync stores only to DB)
                        </label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">API Key</label>
                      <input type="password" className="form-control" value={cfg.pinecone?.api_key || ''} onChange={(e)=>setCfg({...cfg, pinecone: { ...(cfg.pinecone||{}), api_key: e.target.value }})} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Host</label>
                      <input className="form-control" value={cfg.pinecone?.host || ''} onChange={(e)=>setCfg({...cfg, pinecone: { ...(cfg.pinecone||{}), host: e.target.value }})} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Index</label>
                      <input className="form-control" value={cfg.pinecone?.index || ''} onChange={(e)=>setCfg({...cfg, pinecone: { ...(cfg.pinecone||{}), index: e.target.value }})} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {settingsSection === 'connection' && (
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="mb-3">AHRI Connection</h6>
                  <p className="text-muted small mb-4">
                    Configure the OAuth credentials and the GraphQL endpoint used for syncing AHRI data.
                  </p>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="text-uppercase text-muted small fw-bold">OAuth Credentials</div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Token URL</label>
                      <input className="form-control" value={cfg.token_url} onChange={(e)=>setCfg({...cfg, token_url: e.target.value})} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Grant Type</label>
                      <input className="form-control" value={cfg.grant_type} onChange={(e)=>setCfg({...cfg, grant_type: e.target.value})} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Scope</label>
                      <input className="form-control" value={cfg.scope} onChange={(e)=>setCfg({...cfg, scope: e.target.value})} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Client ID</label>
                      <input className="form-control" value={cfg.client_id} onChange={(e)=>setCfg({...cfg, client_id: e.target.value})} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Client Secret</label>
                      <input type="password" className="form-control" value={cfg.client_secret} onChange={(e)=>setCfg({...cfg, client_secret: e.target.value})} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Access Token Override (optional)</label>
                      <input
                        type="password"
                        className="form-control"
                        placeholder="Paste a bearer token to bypass OAuth token call"
                        value={cfg.access_token_override || ''}
                        onChange={(e)=>setCfg({...cfg, access_token_override: e.target.value})}
                      />
                      <div className="form-text">If provided, the app uses this token directly until it expires.</div>
                    </div>
                    <div className="col-12">
                      <hr />
                      <div className="text-uppercase text-muted small fw-bold">Data Fetch Endpoint</div>
                    </div>
                    <div className="col-md-8">
                      <label className="form-label">Data URL</label>
                      <input className="form-control" value={cfg.data_url} onChange={(e)=>setCfg({...cfg, data_url: e.target.value})} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">userUniqueId</label>
                      <input className="form-control" value={cfg.userUniqueId} onChange={(e)=>setCfg({...cfg, userUniqueId: e.target.value})} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Content-Type</label>
                      <input className="form-control" value={cfg.content_type} onChange={(e)=>setCfg({...cfg, content_type: e.target.value})} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Page Size</label>
                      <input type="number" min={1} className="form-control" value={cfg.page_size} onChange={(e)=>{
                        const v = onNum(e.target.value, 1);
                        setCfg({...cfg, page_size: v});
                      }} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Parallel Threads (max 5)</label>
                      <input type="number" min={1} max={5} className="form-control" value={cfg.threads} onChange={(e)=>{
                        const v = onNum(e.target.value, 1, 5);
                        setCfg({...cfg, threads: v});
                      }} />
                    </div>
                    <div className="col-12">
                      <hr />
                      <div className="text-uppercase text-muted small fw-bold">Network & Proxy</div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-check form-switch mt-1">
                        <input className="form-check-input" type="checkbox" id="verify_ssl" checked={!!cfg.verify_ssl} onChange={(e)=>setCfg({...cfg, verify_ssl: e.target.checked})} />
                        <label className="form-check-label" htmlFor="verify_ssl">Verify SSL certificates</label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-check form-switch mt-1">
                        <input className="form-check-input" type="checkbox" id="use_system_proxy" checked={!!cfg.use_system_proxy} onChange={(e)=>setCfg({...cfg, use_system_proxy: e.target.checked})} />
                        <label className="form-check-label" htmlFor="use_system_proxy">Use system proxy</label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Proxy URL (optional)</label>
                      <input
                        className="form-control"
                        placeholder="https://user:pass@proxy.host:port"
                        value={cfg.proxy_url || ''}
                        onChange={(e)=>setCfg({...cfg, proxy_url: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {settingsSection === 'solr' && (
              <>
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="mb-3">SolrCloud (Search & Facets)</h6>
                  <div className="row g-3">
                    <div className="col-md-8">
                      <label className="form-label">Base URL</label>
                      <input
                        className="form-control"
                        placeholder="https://solr-example:8983/solr"
                        value={cfg.solr?.base_url || ''}
                        onChange={(e)=>setCfg({...cfg, solr: { ...(cfg.solr||{}), base_url: e.target.value }})}
                      />
                      <div className="form-text">Point to the SolrCloud root (typically ends with /solr).</div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Verify SSL</label>
                      <div className="form-check form-switch mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="solr_verify_ssl"
                          checked={cfg.solr?.verify_ssl ?? true}
                          onChange={(e)=>setCfg({...cfg, solr: { ...(cfg.solr||{}), verify_ssl: e.target.checked }})}
                        />
                        <label className="form-check-label" htmlFor="solr_verify_ssl">Verify HTTPS certificates</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Username (optional)</label>
                      <input
                        className="form-control"
                        value={cfg.solr?.username || ''}
                        onChange={(e)=>setCfg({...cfg, solr: { ...(cfg.solr||{}), username: e.target.value }})}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Password (optional)</label>
                      <input
                        type="password"
                        className="form-control"
                        value={cfg.solr?.password || ''}
                        onChange={(e)=>setCfg({...cfg, solr: { ...(cfg.solr||{}), password: e.target.value }})}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Shards</label>
                      <input
                        type="number"
                        min={1}
                        className="form-control"
                        value={cfg.solr?.shards ?? 1}
                        onChange={(e)=>{
                          const v = onNum(e.target.value, 1);
                          setCfg({...cfg, solr: { ...(cfg.solr||{}), shards: v }});
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Replicas</label>
                      <input
                        type="number"
                        min={1}
                        className="form-control"
                        value={cfg.solr?.replicas ?? 1}
                        onChange={(e)=>{
                          const v = onNum(e.target.value, 1);
                          setCfg({...cfg, solr: { ...(cfg.solr||{}), replicas: v }});
                        }}
                      />
                    </div>
                    <div className="col-12">
                      <div className="alert alert-info mb-0">
                        Each account gets its own collection named <code>ahri_&lt;accountId&gt;</code>. These settings are required for indexing and Solr search UI.
                      </div>
                    </div>
                    <div className="col-12 d-flex flex-wrap justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={deleteSolrCollection}
                        disabled={solrDeleting}
                      >
                        {solrDeleting ? 'Deleting…' : 'Delete AHRI Data Collection'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-warning"
                        onClick={deleteProductCollection}
                        disabled={productSolrDeleting}
                      >
                        {productSolrDeleting ? 'Deleting…' : 'Delete Products Collection'}
                      </button>
                    </div>
                    <div className="col-12">
                      <hr />
                      <label className="form-label fw-semibold">Delete Data by Program</label>
                      <div className="row g-2 align-items-end">
                        <div className="col-md-6">
                          <input
                            className="form-control"
                            list="ahri-program-list"
                            placeholder="Enter program (e.g., AC, ASHP…) or pick below"
                            value={programPurgeName}
                            onChange={(e)=>setProgramPurgeName(e.target.value)}
                          />
                          <datalist id="ahri-program-list">
                            {programOptions.map((prog) => (
                              <option key={prog} value={prog} />
                            ))}
                          </datalist>
                        </div>
                        <div className="col-md-6">
                          <button
                            type="button"
                            className="btn btn-outline-danger w-100"
                            onClick={handleProgramPurge}
                            disabled={programPurging}
                          >
                            {programPurging ? 'Deleting Program Data…' : 'Delete Program Data'}
                          </button>
                          <div className="form-text">
                            Removes AHRI records for the selected program from PostgreSQL and Solr.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="mb-3">Lookup Transform Rules</h6>
                  <p className="text-muted small mb-4">
                    Configure how AHRI lookup values are cleaned before matching them against product part numbers.
                  </p>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="lt-trim-plus"
                          checked={!!lookupTransform.trim_after_plus}
                          onChange={(e)=>updateLookupTransform({ trim_after_plus: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="lt-trim-plus">
                          Trim everything after "+"
                        </label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Max fallback trim (chars)</label>
                      <input
                        type="number"
                        min={0}
                        className="form-control"
                        value={lookupTransform.truncate_last_chars ?? 0}
                        onChange={(e)=>{
                          const next = Math.max(0, Number(e.target.value) || 0);
                          updateLookupTransform({ truncate_last_chars: next });
                        }}
                      />
                      <div className="form-text">
                        When no match is found, the matcher retries by trimming up to this many trailing characters.
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Replace "*" with</label>
                      <input
                        className="form-control"
                        maxLength={1}
                        value={lookupTransform.asterisk_replacement ?? ''}
                        onChange={(e)=>updateLookupTransform({ asterisk_replacement: e.target.value.slice(0, 1) })}
                      />
                      <div className="form-text">Leave blank to keep "*".</div>
                    </div>
                    <div className="col-12">
                      <div className="form-check form-switch mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="lt-dot-wildcard"
                          checked={!!lookupTransform.dot_wildcard_enabled}
                          onChange={(e)=>updateLookupTransform({ dot_wildcard_enabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="lt-dot-wildcard">
                          Treat "." as a single-character wildcard
                        </label>
                      </div>
                      <div className="form-text">
                        When enabled, any "." in the cleaned value triggers a regex search for single-character wildcards.
                      </div>
                    </div>
                  <hr />
                  <div className="mb-3">
                    <div className="d-flex flex-column flex-lg-row align-items-lg-end gap-3">
                      <div className="flex-grow-1">
                        <label className="form-label">Program for Sample Preview</label>
                        <select
                          className="form-select"
                          value={sampleProgram}
                          onChange={(e)=>setSampleProgram(e.target.value)}
                        >
                          {programOptions.map((prog) => (
                            <option key={prog} value={prog}>{prog}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-grow-1">
                        <label className="form-label">Fields</label>
                        <select
                          className="form-select"
                          multiple
                          value={sampleFields}
                          onChange={(e)=>{
                            const values = Array.from(e.target.selectedOptions).map(opt => opt.value);
                            setSampleFields(values);
                          }}
                          size={Math.min(6, Math.max(3, (programLookupFieldMap[sampleProgram] || []).length))}
                        >
                          {(programLookupFieldMap[sampleProgram] || []).map((field) => (
                            <option key={field} value={field}>{field}</option>
                          ))}
                        </select>
                        <div className="mt-2 d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={()=>setSampleFields(programLookupFieldMap[sampleProgram] || [])}
                          >
                            Select All
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={()=>setSampleFields([])}
                          >
                            Clear
                          </button>
                        </div>
                        <div className="form-text">Hold Cmd/Ctrl to select multiple fields.</div>
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          disabled={sampleLoading || !(programLookupFieldMap[sampleProgram] || []).length}
                          onClick={runSamplePreview}
                        >
                          {sampleLoading ? 'Running…' : 'Preview 5 Records'}
                        </button>
                      </div>
                    </div>
                    {sampleError && <div className="alert alert-danger mt-3 mb-0">{sampleError}</div>}
                    {!sampleLoading && !sampleError && sampleResults.length === 0 && (
                      <div className="text-muted small mt-3">Run the preview to see sample transformations.</div>
                    )}
                    {sampleLoading && (
                      <div className="text-muted small mt-3">Loading sample records…</div>
                    )}
                    {!sampleLoading && sampleResults.length > 0 && (
                      <div className="table-responsive mt-3">
                        <table className="table table-sm align-middle">
                          <thead>
                            <tr>
                              <th style={{ minWidth: 120 }}>AHRI ID</th>
                              <th style={{ minWidth: 80 }}>Program</th>
                              <th style={{ minWidth: 140 }}>Field</th>
                              <th style={{ minWidth: 160 }}>Original Value</th>
                              <th style={{ minWidth: 160 }}>Transformed Value</th>
                              <th style={{ minWidth: 90 }}>Pattern Type</th>
                              <th style={{ minWidth: 200 }}>Pattern</th>
                              <th style={{ minWidth: 220 }}>Fallback Attempts</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sampleResults.map((sample) =>
                              (sample.fields || []).map((fieldEntry, idx) => (
                                <tr key={`${sample.ahri_id}-${fieldEntry.field}-${idx}`}>
                                  <td>{idx === 0 ? sample.ahri_id : ''}</td>
                                  <td>{idx === 0 ? sample.program : ''}</td>
                                  <td><code>{fieldEntry.field}</code></td>
                                  <td>{fieldEntry.original || <span className="text-muted">—</span>}</td>
                                  <td>{fieldEntry.transformed || <span className="text-muted">—</span>}</td>
                                  <td>{fieldEntry.pattern_type || '-'}</td>
                                  <td className="text-break">
                                    {fieldEntry.pattern || <span className="text-muted">n/a</span>}
                                  </td>
                                  <td className="text-break">
                                    {Array.isArray(fieldEntry.attempts) && fieldEntry.attempts.length > 0 ? (
                                      <ul className="mb-0 ps-3 small">
                                        {fieldEntry.attempts.map((attempt, attemptIdx) => (
                                          <li key={`${fieldEntry.field}-attempt-${attemptIdx}`}>
                                            <span className="text-muted">
                                              {attempt.fallback ? 'Fallback → ' : ''}
                                              {attempt.value}
                                            </span>
                                            {attempt.pattern && (
                                              <div>
                                                <code>{attempt.pattern}</code>
                                                <span className="text-muted"> ({attempt.pattern_type})</span>
                                              </div>
                                            )}
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <span className="text-muted">—</span>
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              </div>
              </>
            )}
            {settingsSection === 'match_lookup' && (
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="mb-3">Match Lookup API</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Fallback Product Image Base URL</label>
                      <input
                        className="form-control"
                        placeholder={DEFAULT_PRODUCT_IMAGE_BASE_URL}
                        value={cfg.product_image_fallback_base_url || ''}
                        onChange={(e)=>setCfg({...cfg, product_image_fallback_base_url: e.target.value})}
                      />
                      <div className="form-text">
                        Used to build complete image URLs when Solr only stores a filename.
                      </div>
                    </div>
                    <div className="col-md-6 d-flex align-items-center">
                      <div className="form-check form-switch mt-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="match_lookup_complete_only"
                          checked={!!matchLookupCfg.enforce_complete_match}
                          onChange={(e)=>updateMatchLookup({ enforce_complete_match: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="match_lookup_complete_only">
                          Require <code>ahri_complete_match = true</code>
                        </label>
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Product Fields (one per line)</label>
                      <textarea
                        className="form-control"
                        rows={4}
                        value={(matchLookupCfg.product_fields || []).join('\n')}
                        onChange={(e)=>{
                          const entries = e.target.value
                            .split(/[\n,]/)
                            .map((part) => part.trim())
                            .filter(Boolean);
                          updateMatchLookup({ product_fields: entries });
                        }}
                      />
                      <div className="form-text">
                        These fields are passed to Solr's <code>fl</code> parameter when fetching mapped products.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {settingsSection === 'schemas' && (
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Program Schemas</h6>
                    <button
                      className="btn btn-sm btn-primary"
                    onClick={()=>{
                        setSchemaProg('');
                        setSchemaCollection('');
                        setSchemaLines('');
                        setSchemaProductFields([]);
                        setSchemaSelectedFields([]);
                        const nextTree = createEmptyWhereTree();
                        setQbTree(nextTree);
                        setWhereJsonDirty(false);
                        setWhereJsonError('');
                        setWhereJsonText(JSON.stringify(nextTree, null, 2));
                        setDrawerOpen(true);
                      }}
                    >
                      Add Program Schema
                    </button>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Program</th>
                          <th>Collection</th>
                          <th>Fields</th>
                          <th>Product Lookup Fields</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(cfg.program_schemas || []).map((s, i) => (
                          <tr key={i}>
                            <td><code>{s.program}</code></td>
                            <td>{s.collection || <span className="text-muted">default</span>}</td>
                            <td style={{ maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={(s.fields || []).join(', ')}>
                              {(s.fields || []).join(', ')}
                            </td>
                            <td style={{ maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={(s.product_lookup_fields || []).join(', ')}>
                              {s.product_lookup_fields && s.product_lookup_fields.length > 0 ? s.product_lookup_fields.join(', ') : <span className="text-muted">None</span>}
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={()=>{
                                  setSchemaProg(s.program || '');
                                  setSchemaCollection(s.collection || '');
                                  const lines = (s.fields || []).map(f => `${f}: `).join('\n');
                                  setSchemaLines(lines);
                                  setSchemaProductFields(Array.isArray(s.product_lookup_fields) ? s.product_lookup_fields : []);
                                  setSchemaSelectedFields(Array.isArray(s.fields) ? s.fields : []);
                                  const existingTree = s.where_tree && typeof s.where_tree === 'object'
                                    ? normalizeWhereTree(s.where_tree)
                                    : createEmptyWhereTree();
                                  setQbTree(existingTree);
                                  setWhereJsonDirty(false);
                                  setWhereJsonError('');
                                  setWhereJsonText(JSON.stringify(existingTree, null, 2));
                                  setDrawerOpen(true);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-outline-success me-2"
                                onClick={async ()=>{
                                  const program = s.program || '';
                                  setVerifyProgram(program);
                                  setVerifyOpen(true);
                                  setVerifyLoading(true);
                                  setVerifyError('');
                                  setVerifyData(null);
                                  try {
                                    const r = await fetch(`${API_BASE_URL}/api/v1/ahri/verify`, {
                                      method: 'POST',
                                      headers,
                                      body: JSON.stringify({ program, access_token_override: (cfg.access_token_override || '') })
                                    });
                                    const j = await r.json().catch(()=>({ detail: 'Invalid JSON' }));
                                    if (!r.ok) {
                                      setVerifyError(j?.detail || `HTTP ${r.status}`);
                                    } else {
                                      setVerifyData(j);
                                      if (j && j.success === false && j.error) {
                                        setVerifyError(j.error);
                                      }
                                    }
                                  } catch (e) {
                                    setVerifyError(String(e));
                                  } finally {
                                    setVerifyLoading(false);
                                  }
                                }}
                              >
                                Verify
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => {
                                  const list = (cfg.program_schemas || []).filter(x => (x.program || '').toLowerCase() !== (s.program || '').toLowerCase());
                                  setCfg({ ...cfg, program_schemas: list });
                                  try {
                                    setSchemasText(JSON.stringify(list, null, 2));
                                  } catch {}
                                }}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                        {(!cfg.program_schemas || cfg.program_schemas.length === 0) && (
                          <tr><td colSpan="5" className="text-muted">No program schemas defined.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            <div className="d-flex gap-2 mb-3">
              <button className="btn btn-primary" onClick={save}>Save Settings</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'sync' && (
        <>
          {activeJob && (
            <div className="alert alert-primary d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
              <div>
                <div className="fw-semibold">
                  Current Data Import: <code>{activeJob.job_id}</code> ({activeJob.status})
                </div>
                <div className="small text-muted">Pages processed: {activeJob.pages_completed} • Upserted: {activeJob.items_upserted}</div>
              </div>
              <div className="flex-grow-1" style={{ minWidth: 220 }}>
                <div className="d-flex justify-content-between small mb-1">
                  <span>Progress</span>
                  <span>{activeJobProgress.label} ({activeJobProgress.percent}%)</span>
                </div>
                <div className="progress" style={{ height: 6 }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${activeJobProgress.percent}%` }}
                    aria-valuenow={activeJobProgress.percent}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  />
                </div>
              </div>
            </div>
          )}
          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-3 align-items-start">
                <div className="col-lg-8">
                  <label className="form-label">Programs to Sync</label>
                  <select
                    multiple
                    className="form-select"
                    size={Math.min(8, Math.max(programOptions.length || 1, 4))}
                    value={selectedPrograms}
                    onChange={handleProgramSelectChange}
                  >
                    {programOptions.map((prog) => (
                      <option key={prog} value={prog}>{prog}</option>
                    ))}
                  </select>
                  <div className="form-text">
                    Hold Ctrl/Cmd to select multiple programs. Leaving all selected runs the sync for every program schema.
                  </div>
                </div>
                <div className="col-lg-4 d-flex flex-column gap-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={handleSelectAllPrograms}>
                    Select All
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={handleClearPrograms}>
                    Clear Selection
                  </button>
                  <div className="small text-muted">
                    Sync will only run for the highlighted programs.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex gap-2 mb-3">
            <button className="btn btn-outline-secondary" onClick={startSync} disabled={!selectedPrograms.length}>
              Start Sync
            </button>
            <button className="btn btn-outline-success" onClick={startEmbeddings} disabled={!cfg.vectorize_enabled} title={!cfg.vectorize_enabled ? 'Enable vectorizing in settings to allow embeddings' : ''}>Start Embeddings</button>
            <div className="form-check ms-2">
              <input className="form-check-input" type="checkbox" id="embdbg" checked={embDebug} onChange={(e)=>setEmbDebug(e.target.checked)} />
              <label className="form-check-label" htmlFor="embdbg">Debug</label>
            </div>
            <div className="form-check ms-auto">
              <input className="form-check-input" type="checkbox" id="poll" checked={polling} onChange={(e)=>setPolling(e.target.checked)} />
              <label className="form-check-label" htmlFor="poll">Auto-refresh jobs (5s)</label>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h6 className="mb-3">Recent Jobs</h6>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                <tr>
                      <th>Job</th>
                      <th>Status</th>
                  <th>Progress</th>
                  <th>Current</th>
                      <th>Pages</th>
                      <th>Upserted</th>
                      <th>Page Size</th>
                      <th>Threads</th>
                      <th>Started</th>
                      <th>Updated</th>
                      <th>Error</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map(j => {
                      const canCancel = j.status === 'pending' || j.status === 'running';
                      const canDelete = !canCancel; // allow delete when not in progress
                      return (
                    <tr key={j.job_id}>
                          <td><code>{j.job_id}</code></td>
                          <td>{j.status}</td>
                          <td>{j.progress}{typeof j.total === 'number' ? ` / ${j.total}` : ''}</td>
                      <td>
                        {(() => {
                          const em = j.error_message || '';
                          const lines = em.split(/\r?\n/).filter(Boolean);
                          const pull = [...lines].reverse().find(l => l.startsWith('pull '));
                          if (!pull) return <span className="text-muted">—</span>;
                          // parse like: pull program=X status=Y outdoor=Z indoor=Q page=N (allow spaces)
                          const m = pull.match(/program=(.*?)\\s+status=(.*?)\\s+outdoor=(.*?)\\s+indoor=(.*?)\\s+page=(\\d+)/);
                          const program = m?.[1] || '';
                          const status = m?.[2] || '';
                          const outdoor = m?.[3] || '';
                          const indoor = m?.[4] || '';
                          const page = m?.[5] || '';
                          return (
                            <div className="d-flex flex-column">
                              <div className="d-flex flex-wrap gap-1">
                                <span className="card badge bg-primary me-1 mb-1">Program: {program}</span>
                                <span className="card badge bg-secondary me-1 mb-1">Status: {status}</span>
                                <span className="card badge bg-info text-dark me-1 mb-1">Outdoor: {outdoor}</span>
                                <span className="card badge bg-info text-dark me-1 mb-1">Indoor: {indoor}</span>
                                <span className="card badge bg-warning text-dark mb-1">Page: {page}</span>
                              </div>
                              {typeof j.total === 'number' && typeof j.progress === 'number' && j.total > 0 && (
                                <div className="progress mt-1" style={{ height: 6, maxWidth: 220 }}>
                                  <div className="progress-bar" role="progressbar" style={{ width: `${Math.min(100, Math.round(j.progress / j.total * 100))}%` }} aria-valuenow={j.progress} aria-valuemin="0" aria-valuemax={j.total} />
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                          <td>{j.pages_completed}</td>
                          <td>{j.items_upserted}</td>
                          <td>{j.page_size}</td>
                          <td>{j.threads}</td>
                          <td>{j.started_at ? new Date(j.started_at).toLocaleString() : ''}</td>
                          <td>{j.updated_at ? new Date(j.updated_at).toLocaleString() : ''}</td>
                      <td style={{maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} title={(j.error_message || '').replace(/\r?\n/g,' ')}>
                        {j.error_message}
                      </td>
                          <td className="d-flex gap-2">
                            <button className="btn btn-sm btn-outline-danger" disabled={!canCancel} onClick={()=>cancelJob(j.job_id)}>Cancel</button>
                            <button className="btn btn-sm btn-outline-secondary" disabled={!canDelete} onClick={()=>deleteJob(j.job_id)}>Delete</button>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          disabled={!j.error_message}
                          onClick={() => { setLogText(j.error_message || ''); setLogOpen(true); }}
                          title="View progress / error log"
                        >
                          View Log
                        </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Log Modal */}
      <div className={`modal fade ${logOpen ? 'show d-block' : ''}`} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Job Log</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={()=>setLogOpen(false)} />
            </div>
            <div className="modal-body">
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{logText || 'No log available'}</pre>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={()=>setLogOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      </div>
      {/* Verify Modal */}
      <div className={`modal fade ${verifyOpen ? 'show d-block' : ''}`} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-xl" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Verify Program {verifyProgram ? `- ${verifyProgram}` : ''}</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={()=>setVerifyOpen(false)} />
            </div>
            <div className="modal-body">
              {verifyLoading && <div className="text-muted">Verifying...</div>}
              {!verifyLoading && (
                <>
                  {verifyError && (
                    <div className="alert alert-danger">
                      {verifyError}
                    </div>
                  )}
                  {verifyData && (
                    <>
                      <div className="mb-2">
                        <div className="row g-2">
                          <div className="col-sm-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="small text-muted">Success</div>
                                <div className="fw-bold">{String(verifyData.success)}</div>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="small text-muted">Total Count</div>
                                <div className="fw-bold">{verifyData.totalCount ?? '—'}</div>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="small text-muted">Returned (Page 1)</div>
                                <div className="fw-bold">{verifyData.returned ?? (Array.isArray(verifyData.items) ? verifyData.items.length : '—')}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <div className="row g-2">
                          <div className="col-sm-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="small text-muted">Auth Used</div>
                                <div className="fw-bold">
                                  {(verifyData?.debug?.auth_used) || (verifyData?.auth_used) || '—'}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="small text-muted">Auth Source</div>
                                <div className="fw-bold">
                                  {(verifyData?.debug?.auth_source) || (verifyData?.auth_source) || '—'}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="small text-muted">Transport</div>
                                <div className="fw-bold">
                                  {(verifyData?.debug?.transport) || (verifyData?.transport) || '—'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {Array.isArray(verifyData.items) && verifyData.items.length > 0 && (
                        <div className="mb-3">
                          <h6>Items (preview)</h6>
                          <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 320, overflow: 'auto' }}>
{JSON.stringify(verifyData.items.slice(0, 10), null, 2)}
                          </pre>
                        </div>
                      )}
                      <div className="accordion" id="verifyAcc">
                        <div className="accordion-item">
                          <h2 className="accordion-header" id="v-gql">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#v-gql-c" aria-expanded="false" aria-controls="v-gql-c">
                              GraphQL Queries
                            </button>
                          </h2>
                          <div id="v-gql-c" className="accordion-collapse collapse" aria-labelledby="v-gql" data-bs-parent="#verifyAcc">
                            <div className="accordion-body">
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <div className="small text-muted">Inline Query</div>
                                  <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
{(verifyData?.request_inline?.query) || (verifyData?.debug?.request_inline?.query) || '(none)'}
                                  </pre>
                                </div>
                                <div className="col-md-6">
                                  <div className="small text-muted">Variables Query</div>
                                  <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
{(verifyData?.request_vars?.query) || (verifyData?.debug?.request_vars?.query) || '(none)'}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="accordion-item">
                          <h2 className="accordion-header" id="v-req">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#v-req-c" aria-expanded="false" aria-controls="v-req-c">
                              Request Bodies
                            </button>
                          </h2>
                          <div id="v-req-c" className="accordion-collapse collapse" aria-labelledby="v-req" data-bs-parent="#verifyAcc">
                            <div className="accordion-body">
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <div className="small text-muted">Inline</div>
                                  <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
{JSON.stringify(verifyData.request_inline || (verifyData.debug && verifyData.debug.request_inline) || {}, null, 2)}
                                  </pre>
                                </div>
                                <div className="col-md-6">
                                  <div className="small text-muted">Variables</div>
                                  <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
{JSON.stringify(verifyData.request_vars || (verifyData.debug && verifyData.debug.request_vars) || {}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="accordion-item">
                          <h2 className="accordion-header" id="v-resp">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#v-resp-c" aria-expanded="false" aria-controls="v-resp-c">
                              Responses
                            </button>
                          </h2>
                          <div id="v-resp-c" className="accordion-collapse collapse" aria-labelledby="v-resp" data-bs-parent="#verifyAcc">
                            <div className="accordion-body">
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <div className="small text-muted">Inline</div>
                                  <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
{JSON.stringify((verifyData.debug && verifyData.debug.response) || verifyData.inline_response || {}, null, 2)}
                                  </pre>
                                </div>
                                <div className="col-md-6">
                                  <div className="small text-muted">Variables</div>
                                  <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
{JSON.stringify(verifyData.response_vars || {}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                              {verifyData.inline_status && (
                                <div className="mt-2 small text-muted">Inline status: {verifyData.inline_status}</div>
                              )}
                              {verifyData.vars_status && (
                                <div className="mt-1 small text-muted">Vars status: {verifyData.vars_status}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={()=>setVerifyOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      </div>
      {/* Confirm Save Schema Modal */}
      <div className={`modal fade ${confirmSchemaOpen ? 'show d-block' : ''}`} tabIndex="-1" role="dialog">
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Save</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={()=>setConfirmSchemaOpen(false)} />
            </div>
            <div className="modal-body">
              <div className="mb-2">Save schema for program <code>{pendingSchema?.prog}</code>?</div>
              {pendingSchema && (
                <ul className="small mb-0">
                  <li>Collection: <code>{pendingSchema.entry.collection || 'default'}</code></li>
                  <li>Fields: {Array.isArray(pendingSchema.entry.fields) ? pendingSchema.entry.fields.length : 0}</li>
                  <li>Product Lookup Fields: {Array.isArray(pendingSchema.entry.product_lookup_fields) ? pendingSchema.entry.product_lookup_fields.length : 0}</li>
                  <li>Where Builder: {pendingSchema.entry.where_tree ? 'configured' : 'none'}</li>
                </ul>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={()=>setConfirmSchemaOpen(false)}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async ()=>{
                  if (!pendingSchema) { setConfirmSchemaOpen(false); return; }
                  const { prog, entry } = pendingSchema;
                  const list = Array.isArray(cfg.program_schemas) ? [...cfg.program_schemas] : [];
                  const idx = list.findIndex(x => (x.program || '').toLowerCase() === prog.toLowerCase());
                  if (idx >= 0) list[idx] = { ...list[idx], ...entry }; else list.push(entry);
                  const newCfg = { ...cfg, program_schemas: list };
                  setCfg(newCfg);
                  try { setSchemasText(JSON.stringify(list, null, 2)); } catch {}
                  // persist immediately so collection is saved to DB
                  try {
                    const r = await fetch(`${API_BASE_URL}/api/v1/ahri/config`, {
                      method: 'PUT',
                      headers,
                      body: JSON.stringify(newCfg)
                    });
                    if (!r.ok) {
                      const j = await r.json().catch(()=>({}));
                      alert(`Save failed: ${j?.detail || r.status}`);
                    }
                  } catch (e) {
                    alert(`Save failed: ${String(e)}`);
                  }
                  setConfirmSchemaOpen(false);
                  setPendingSchema(null);
                  setDrawerOpen(false);
                }}
              >
                Confirm Save
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Slide-out drawer for program schema (right to left) */}
      <div className={`offcanvas offcanvas-end ${drawerOpen ? 'show' : ''}`} style={{ visibility: drawerOpen ? 'visible' : 'hidden', width: `${drawerWidthPct}vw`, maxWidth: '75vw' }}>
        {/* Resize handle */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '6px',
            cursor: 'col-resize',
            background: 'rgba(0,0,0,0.05)'
          }}
          onMouseDown={() => setResizing(true)}
          title="Drag to resize (50% - 75%)"
        />
        <div className="offcanvas-header">
          <h5 className="offcanvas-title">Add Program Schema</h5>
          <button type="button" className="btn-close" aria-label="Close" onClick={()=>setDrawerOpen(false)}></button>
        </div>
        <div className="offcanvas-body">
          <div className="mb-3">
            <label className="form-label">Program</label>
            <input className="form-control" value={schemaProg} onChange={e=>setSchemaProg(e.target.value)} placeholder="e.g., ULE" />
          </div>
          <div className="mb-3">
            <label className="form-label">Collection (optional)</label>
            <input className="form-control" value={schemaCollection} onChange={e=>setSchemaCollection(e.target.value)} placeholder="e.g., uleResults" />
          </div>
          <div className="mb-2 small text-muted">Paste fields and types (one per line): fieldName: Type</div>
          <div className="mb-3">
            <textarea className="form-control" rows={8} value={schemaLines} onChange={e=>setSchemaLines(e.target.value)} placeholder={`referenceId: Long\nmodelStatus: String`} />
          </div>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label mb-0">Fields to Fetch</label>
              {schemaFieldOptions.length > 0 && (
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setSchemaSelectedFields([...schemaFieldOptions])}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setSchemaSelectedFields([])}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
            <div className="border rounded p-2" style={{ maxHeight: 200, overflowY: 'auto' }}>
              {schemaFieldOptions.length === 0 ? (
                <div className="text-muted small">Add fields above to manage selection.</div>
              ) : (
                schemaFieldOptions.map((field) => {
                  const inputId = `schema-select-field-${field}`;
                  return (
                    <div className="form-check" key={field}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={inputId}
                        checked={schemaSelectedFields.includes(field)}
                        onChange={() =>
                          setSchemaSelectedFields((prev) =>
                            prev.includes(field)
                              ? prev.filter((f) => f !== field)
                              : [...prev, field]
                          )
                        }
                      />
                      <label className="form-check-label" htmlFor={inputId}>
                        {field}
                      </label>
                    </div>
                  );
                })
              )}
            </div>
            <div className="form-text">
              Only the selected fields will be requested from the AHRI API for this program.
            </div>
          </div>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label mb-0">Product Lookup &amp; Mapping Fields</label>
              {schemaFieldOptions.length > 0 && (
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setSchemaProductFields([...schemaFieldOptions])}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setSchemaProductFields([])}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
            <div className="border rounded p-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
              {schemaFieldOptions.length === 0 ? (
                <div className="text-muted small">Add fields above to enable product lookup selection.</div>
              ) : (
                schemaFieldOptions.map((field) => {
                  const inputId = `schema-product-field-${field}`;
                  return (
                    <div className="form-check" key={field}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={inputId}
                        checked={schemaProductFields.includes(field)}
                        onChange={() =>
                          setSchemaProductFields((prev) =>
                            prev.includes(field)
                              ? prev.filter((f) => f !== field)
                              : [...prev, field]
                          )
                        }
                      />
                      <label className="form-check-label" htmlFor={inputId}>
                        {field}
                      </label>
                    </div>
                  );
                })
              )}
            </div>
            <div className="form-text">
              These fields are used when looking up or mapping products derived from this program’s data.
            </div>
          </div>
          
          {/* Visual Where Builder */}
          <hr />
          <h6>Where Builder (AND / OR, nested groups)</h6>
          <div className="mb-2 small text-muted">Build advanced where conditions. These will be used as a GraphQL where clause. If provided, simple equality filters for outdoor/indoor/status will be omitted.</div>
          {(() => {
            const ops = ['eq','ne','gt','gte','lt','lte','in','contains','startsWith','endsWith'];
            const parseFieldNames = () => {
              const names = new Set();
              (schemaLines || '').split('\n').forEach(line => {
                const m = String(line||'').match(/^\s*([A-Za-z_][A-Za-z0-9_]*)\s*:/);
                if (m && m[1]) names.add(m[1]);
              });
              return Array.from(names);
            };
            const candidateFields = parseFieldNames();
            const updateGroupAt = (path, mutator) => {
              setQbTree(prev => {
                const copy = JSON.parse(JSON.stringify(prev || { logic:'AND', conditions:[], groups:[] }));
                let node = copy;
                for (const idx of path) {
                  if (!node.groups) node.groups = [];
                  node = node.groups[idx];
                }
                mutator(node);
                return copy;
              });
            };
            const renderGroup = (group, path = []) => (
              <div className="border rounded p-2 mb-2" style={{ borderColor: '#ddd' }}>
                <div className="d-flex align-items-center mb-2">
                  <label className="me-2">Logic</label>
                  <select
                    className="form-select form-select-sm"
                    style={{ maxWidth: 120 }}
                    value={group.logic || 'AND'}
                    onChange={e => updateGroupAt(path, g => { g.logic = e.target.value; })}
                  >
                    <option value="AND">AND</option>
                    <option value="OR">OR</option>
                  </select>
                  {path.length > 0 && (
                    <button
                      className="btn btn-sm btn-outline-danger ms-auto"
                      onClick={() => {
                        const parentPath = path.slice(0, -1);
                        const idx = path[path.length - 1];
                        updateGroupAt(parentPath, p => {
                          p.groups.splice(idx, 1);
                        });
                      }}
                    >Remove group</button>
                  )}
                </div>
                <div className="mb-2">
                  {(group.conditions || []).map((c, ci) => (
                    <div className="d-flex align-items-center mb-2" key={ci}>
                      <select
                        className="form-select form-select-sm me-2"
                        value={c.field || ''}
                        onChange={e => updateGroupAt(path, g => { g.conditions[ci].field = e.target.value; })}
                        style={{ maxWidth: 240 }}
                      >
                        <option value="">Select field...</option>
                        {candidateFields.map(f => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                      <select
                        className="form-select form-select-sm me-2"
                        style={{ maxWidth: 140 }}
                        value={c.op || 'eq'}
                        onChange={e => updateGroupAt(path, g => { g.conditions[ci].op = e.target.value; })}
                      >
                        {ops.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <input
                        className="form-control form-control-sm me-2"
                        value={c.value ?? ''}
                        placeholder={c.op === 'in' ? 'v1, v2, v3' : 'value'}
                        onChange={e => updateGroupAt(path, g => { g.conditions[ci].value = e.target.value; })}
                      />
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => updateGroupAt(path, g => { g.conditions.splice(ci, 1); })}
                      >✕</button>
                    </div>
                  ))}
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => updateGroupAt(path, g => { (g.conditions = g.conditions || []).push({ field: '', op: 'eq', value: '' }); })}
                  >+ Condition</button>
                </div>
                <div className="ms-3">
                  {(group.groups || []).map((sg, gi) => (
                    <div key={gi}>
                      {renderGroup(sg, [...path, gi])}
                    </div>
                  ))}
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => updateGroupAt(path, g => { (g.groups = g.groups || []).push({ logic: 'AND', conditions: [], groups: [] }); })}
                  >+ Group</button>
                </div>
              </div>
            );
            return (
              <div className="mb-3">
                {renderGroup(qbTree, [])}
                <div className="d-flex justify-content-between">
                <div className="small text-muted">{hasWhereContent(qbTree) ? 'Conditions configured' : 'No conditions'}</div>
                  <button
                    className="btn btn-sm btn-outline-warning"
                  onClick={() => {
                    const resetTree = createEmptyWhereTree();
                    setQbTree(resetTree);
                    setWhereJsonDirty(false);
                    setWhereJsonError('');
                    setWhereJsonText(JSON.stringify(resetTree, null, 2));
                  }}
                  >
                    Reset Builder
                  </button>
                </div>
              </div>
            );
          })()}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label mb-0">Where Builder JSON</label>
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={()=>{
                    try {
                      const parsed = JSON.parse(whereJsonText || '{}');
                      const normalized = normalizeWhereTree(parsed);
                      setQbTree(normalized);
                      setWhereJsonDirty(false);
                      setWhereJsonError('');
                      setWhereJsonText(JSON.stringify(normalized, null, 2));
                    } catch (err) {
                      setWhereJsonError(String(err.message || err));
                    }
                  }}
                >
                  Apply JSON
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={()=>{
                    const normalized = normalizeWhereTree(qbTree);
                    setWhereJsonDirty(false);
                    setWhereJsonError('');
                    setWhereJsonText(JSON.stringify(normalized, null, 2));
                  }}
                >
                  Reset JSON
                </button>
              </div>
            </div>
            <textarea
              className={`form-control ${whereJsonError ? 'is-invalid' : ''}`}
              rows={8}
              value={whereJsonText}
              onChange={(e)=>{
                setWhereJsonDirty(true);
                setWhereJsonText(e.target.value);
              }}
            />
            <div className="form-text">
              View or edit the raw where-tree JSON if you prefer direct control. Click &ldquo;Apply JSON&rdquo; to update the visual builder.
            </div>
            {whereJsonError && <div className="invalid-feedback d-block">{whereJsonError}</div>}
          </div>
          <div className="d-flex gap-2 mt-3">
            <button
              className="btn btn-primary"
              onClick={()=>{
                const prog = (schemaProg || '').trim();
                if (!prog) { alert('Program is required'); return; }
                const fieldsFromLines = [];
                (schemaLines || '').split('\n').forEach(line=>{
                  const m = String(line||'').match(/^\s*([A-Za-z_][A-Za-z0-9_]*)\s*:/);
                  if (m && m[1]) fieldsFromLines.push(m[1]);
                });
                if (fieldsFromLines.length === 0) { alert('No valid fields found'); return; }
                const fetchFields = schemaSelectedFields.length > 0 ? schemaSelectedFields : fieldsFromLines;
                if (fetchFields.length === 0) { alert('Select at least one field to fetch'); return; }
                const cleanedProductFields = Array.from(new Set(schemaProductFields.map(f => (f || '').trim()))).filter(Boolean);
                const entry = {
                  program: prog,
                  collection: (schemaCollection || '').trim(),
                  fields: fetchFields,
                  product_lookup_fields: cleanedProductFields,
                };
                // attach where_tree if it has content
                if (hasWhereContent(qbTree)) {
                  entry['where_tree'] = qbTree;
                }
                setPendingSchema({ prog, entry });
                setConfirmSchemaOpen(true);
              }}
            >
              Save
            </button>
            <button className="btn btn-outline-secondary" onClick={()=>setDrawerOpen(false)}>Cancel</button>
          </div>
        </div>
      </div>
      {activeTab === 'mapping' && (
        <div className="card">
          <div className="card-body">
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
              <div>
                <h5 className="mb-1">AHRI Product Mapping</h5>
                <div className="text-muted small">
                  Uses program-specific lookup fields to locate products (via regex) and store the relationships for Solr faceting.
                </div>
              </div>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={loadMappingState}
                  disabled={mappingLoading}
                >
                  {mappingLoading ? 'Refreshing…' : 'Refresh'}
                </button>
                <button
                  className="btn btn-outline-dark"
                  onClick={()=>{
                    const lines = Array.isArray(mappingState?.log) ? mappingState.log : [];
                    setLogText(lines.length ? lines.join('\n') : 'No log entries yet');
                    setLogOpen(true);
                  }}
                  disabled={!Array.isArray(mappingState?.log) || mappingState.log.length === 0}
                >
                  View Log
                </button>
                {mappingActive && (
                  <button
                    className="btn btn-outline-danger"
                    onClick={stopProductMapping}
                    disabled={mappingStopping}
                  >
                    {mappingStopping ? 'Stopping…' : 'Stop Mapping'}
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={startProductMapping}
                  disabled={
                    mappingStarting
                    || mappingActive
                    || (mappingScopeSelection === 'subset' && mappingIdsParsedCount === 0)
                  }
                >
                  {mappingActive ? 'Mapping in progress…' : 'Start Mapping'}
                </button>
              </div>
            </div>
            {mappingError && (
              <div className="alert alert-danger">{mappingError}</div>
            )}
            <div className="row g-3 mb-3">
              <div className="col-sm-6 col-lg-3">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Status</div>
                  <div className="fw-semibold text-capitalize">{mappingStatus || 'idle'}</div>
                  {mappingState?.job_id && (
                    <div className="small text-muted mt-1">
                      Job: <code>{mappingState.job_id}</code>
                    </div>
                  )}
                  <div className="small text-muted mt-1">
                    Scope: {mappingScopeState === 'subset' ? 'Selected AHRI IDs' : 'Complete AHRI data'}
                  </div>
                  <div className="small text-muted">
                    Threads: {mappingState?.mapping_threads || cfg.mapping_threads || 2}
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Processed</div>
                  <div className="fw-semibold">{mappingStats.processed} / {mappingStats.total || '—'}</div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Matches</div>
                  <div className="fw-semibold">{mappingMatches}</div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Complete Matches</div>
                  <div className="fw-semibold">{mappingCompleteMatches}</div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Products Linked</div>
                  <div className="fw-semibold">{mappingUpdatedProducts}</div>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <div className="d-flex justify-content-between small text-muted mb-1">
                <span>Progress</span>
                <span>{mappingStats.percent}%</span>
              </div>
              <div className="progress" style={{ height: 6 }}>
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${mappingStats.percent}%` }}
                  aria-valuenow={mappingStats.percent}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Started</div>
                  <div>{mappingState?.started_at ? new Date(mappingState.started_at).toLocaleString() : '—'}</div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Last Update</div>
                  <div>{mappingState?.updated_at ? new Date(mappingState.updated_at).toLocaleString() : '—'}</div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Last AHRI ID</div>
                  <div>{mappingState?.last_ahri_id || '—'}</div>
                </div>
              </div>
            </div>
            {mappingScopeState === 'subset' && (
              <div className="alert alert-warning mb-3">
                <div className="fw-semibold mb-1">Targeted mapping run</div>
                <div className="small">
                  Evaluating {mappingRequestedIdsTotal} AHRI ID{mappingRequestedIdsTotal === 1 ? '' : 's'}
                  {mappingRequestedIdsMore > 0 ? ' (preview below)' : ''}
                </div>
                {mappingRequestedIds.length > 0 && (
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {mappingRequestedIds.map((id) => (
                      <code key={id} className="bg-light px-2 py-1 rounded">{id}</code>
                    ))}
                    {mappingRequestedIdsMore > 0 && (
                      <span className="text-muted small align-self-center">
                        +{mappingRequestedIdsMore} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="card mb-3">
              <div className="card-body">
                <h6 className="mb-3">Mapping Scope</h6>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="mapping-scope"
                    id="mapping-scope-full"
                    value="full"
                    checked={mappingScopeSelection === 'full'}
                    onChange={() => setMappingScopeSelection('full')}
                    disabled={mappingActive}
                  />
                  <label className="form-check-label" htmlFor="mapping-scope-full">
                    Map the entire AHRI dataset
                  </label>
                </div>
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="mapping-scope"
                    id="mapping-scope-subset"
                    value="subset"
                    checked={mappingScopeSelection === 'subset'}
                    onChange={() => setMappingScopeSelection('subset')}
                    disabled={mappingActive}
                  />
                  <label className="form-check-label" htmlFor="mapping-scope-subset">
                    Map only specific AHRI IDs
                  </label>
                </div>
                {mappingScopeSelection === 'subset' && (
                  <div>
                    <label className="form-label">AHRI IDs (comma or newline separated)</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={mappingIdsInput}
                      onChange={(e)=>setMappingIdsInput(e.target.value)}
                      placeholder="215271984, 215271985, 215271986"
                      disabled={mappingActive}
                    />
                    <div className="form-text">
                      {mappingIdsParsedCount > 0
                        ? `${mappingIdsParsedCount} ID${mappingIdsParsedCount === 1 ? '' : 's'} detected.`
                        : 'Enter at least one AHRI ID.'}
                    </div>
                  </div>
                )}
                <div className="row g-3 mt-3">
                  <div className="col-sm-6 col-md-4 col-lg-3">
                    <label className="form-label">Mapping Threads</label>
                    <input
                      type="number"
                      className="form-control"
                      min={1}
                      max={10}
                      value={cfg.mapping_threads ?? 2}
                      onChange={(e) => handleMappingThreadsChange(e.target.value)}
                      disabled={mappingActive}
                    />
                    <div className="form-text">
                      Use 1-10 threads for mapping. Higher values increase Solr load.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card mb-3">
              <div className="card-body">
                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-3">
                  <h6 className="mb-0">Recent Mapping Jobs</h6>
                  <div className="btn-group">
                    <button className="btn btn-outline-secondary btn-sm" onClick={loadMappingJobs} disabled={mappingJobsLoading}>
                      {mappingJobsLoading ? 'Refreshing…' : 'Refresh History'}
                    </button>
                  </div>
                </div>
                {mappingJobsError && <div className="alert alert-danger">{mappingJobsError}</div>}
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Job ID</th>
                        <th>Status</th>
                        <th>Processed</th>
                        <th>Matches</th>
                        <th>Products</th>
                        <th>Started</th>
                        <th>Finished</th>
                        <th style={{ width: 160 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappingJobs.map((job) => (
                        <tr key={job.job_id}>
                          <td><code style={{ fontSize: '0.8rem' }}>{job.job_id}</code></td>
                          <td className="text-capitalize">{job.status || '—'}</td>
                          <td>{job.processed ?? 0}{typeof job.total === 'number' ? ` / ${job.total}` : ''}</td>
                          <td>{job.matches ?? 0}</td>
                          <td>{job.updated_products ?? 0}</td>
                          <td>{job.started_at ? new Date(job.started_at).toLocaleString() : '—'}</td>
                          <td>{job.finished_at ? new Date(job.finished_at).toLocaleString() : <span className="text-muted">—</span>}</td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button className="btn btn-outline-primary" onClick={() => viewMappingJobLog(job.job_id)}>
                                View Log
                              </button>
                              <button className="btn btn-outline-danger" onClick={() => deleteMappingJob(job.job_id)}>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {(!mappingJobs || mappingJobs.length === 0) && (
                        <tr>
                          <td colSpan="8" className="text-muted text-center">No jobs recorded yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="alert alert-info mb-0">
              Product mapping evaluates each AHRI record, uses the configured lookup fields per program schema, runs a regex search against the product Solr collection (`partnumber_regex`), and stores the matches back into both AHRI Solr (dynamic fields) and the product records (`ahri_ids`). This enables faceting/searching across both collections by shared identifiers.
            </div>
          </div>
        </div>
      )}
      {drawerOpen && <div className="offcanvas-backdrop fade show" onClick={()=>setDrawerOpen(false)}></div>}
    </div>
  );
};

export default AhriData;


